import { IndexedDbStores, purgeIndexedDBStore } from '@frontend/indexed-db';

import LoggingDatabase from './logging-db-connection';
import { Log, LogViewModel } from './models/log';
import { LogMessageTypeName } from './models/log-message-type';

const environment = process.env['NODE_ENV'];
const isProd = environment === 'production';
export class Logger {
    public static saveLogs = false;
    static async debug(message?: any, ...optionalParams: any[]) {
        !isProd && console.debug(message, optionalParams, this?.getSource());
        this?.save(message, LogMessageTypeName.DEBUG, ...optionalParams);
    }

    static async log(message?: any, ...optionalParams: any[]) {
        console.log(message, optionalParams, this?.getSource());
        this?.save(message, LogMessageTypeName.INFO, ...optionalParams);
    }

    static async warn(message?: any, ...optionalParams: any[]) {
        console.warn(message, optionalParams, this?.getSource());
        this?.save(message, LogMessageTypeName.WARNING, ...optionalParams);
    }

    static async error(message?: any, ...optionalParams: any[]) {
        console.error(message, optionalParams, this?.getSource());
        this?.save(message, LogMessageTypeName.ERROR, ...optionalParams);
    }

    private static async save(message: string | any, level: LogMessageTypeName, ...optionalParams: any[]) {
        let actualMessage = '';
        if (message instanceof Error) {
            actualMessage = message.message;
        }

        const log =
            typeof message === 'string'
                ? new Log(message, level, optionalParams && JSON.stringify({ ...optionalParams }))
                : new Log(actualMessage, level, optionalParams && JSON.stringify([message, { ...optionalParams }]));

        this?.saveLog(log).then((success) => {
            if (!success) console.log('log was not saved');
        });
    }

    private static async saveLog(log: Log): Promise<boolean> {
        if (!this.saveLogs) {
            try {
                await purgeIndexedDBStore(IndexedDbStores.LOGS);
            } catch (error) {
                console.error('Failed to purge IndexedDB logs:', error);
            }
            return true;
        }
        return await LoggingDatabase.saveLog(log);
    }
    static async getBasicLogs(): Promise<Log[]> {
        const logs = await LoggingDatabase.getLogs();
        return logs || [];
    }
    static async getLogs(): Promise<LogViewModel[] | undefined> {
        const logs = await LoggingDatabase.getLogs();
        return logs?.map(Log.parseToViewModel);
    }
    static async removeByids(ids: number[]): Promise<void> {
        return LoggingDatabase.removeById(ids);
    }

    /**
     * This function should be used on sensitive data before it is logged. (e.g., access-tokens, passwords, ...)
     * @param value
     * @returns
     */
    static async generateHash(value: string): Promise<string> {
        return await sha256(value);
    }

    private static getSource() {
        try {
            return new Error().stack?.split('\n')[2].trim() || 'Unknown';
        } catch {
            return undefined;
        }
    }
}

export async function sha256(str: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);

    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');

    return hashHex;
}
